import dayjs from 'dayjs'
import { ActivityType } from '../../../../../pure-js/enums/ActivityType'
import { ONE_MINUTE_MS } from '../../../../../pure-js/libs/Consts'
import { isAIDebriefActivity, isGameActivityType } from '../../../../../pure-js/libs/isAIDebriefActivity'
import { ActivitySummary, ActivityWithSession, ActivityWithSessions } from '../../../types/GeneralTypes'
import { hasPlayedEveryConfiguredLoop } from './gameSessionLatch'

/**
 * Check if current time is within 5 minutes before activity start time
 * @param activity - Activity to check
 * @returns true if within 5 minutes before start, false otherwise
 */
export const isWithin5MinutesBeforeStart = (activity: ActivityWithSessions): boolean => {
  if (!activity.startDate) {
    return false
  }

  const now = dayjs()
  const startTime = dayjs(activity.startDate)
  const fiveMinsBefore = startTime.subtract(5, 'minutes')

  // Check if current time is after or equal to 5 minutes before start time
  return now.isAfter(fiveMinsBefore) || now.isSame(fiveMinsBefore)
}

/**
 * Check if activity should show the "Go Dashboard" button
 * Conditions:
 * 1. Activity type must be: SHORT_REFLECTION, LONG_GAME, FIRST_GAME, or SHORT_GAME
 * 2. Current time must be within 5 minutes before activity start time
 * @param activity - Activity to check
 * @returns true if conditions are met, false otherwise
 */
export const shouldShowGoDashboardButton = (activity: ActivityWithSessions): boolean => {
  // Check if activity type matches allowed types
  const allowedTypes = [
    ActivityType.SHORT_REFLECTION,
    ActivityType.LONG_GAME,
    ActivityType.FIRST_GAME,
    ActivityType.SHORT_GAME
  ]

  if (!activity.type || !allowedTypes.includes(activity.type as ActivityType)) {
    return false
  }

  // Check timing condition
  return isWithin5MinutesBeforeStart(activity)
}

export type NextActivityChoice = { activity: ActivityWithSession; isFromFallback: boolean }

// The current activity; failing that, the first game the team still has loops to play.
export const pickNextActivityForCard = (
  currentActivity: ActivityWithSession | undefined,
  upcomingActivities: ActivityWithSessions[],
  userTeamIds: string[]
): NextActivityChoice | null => {
  const isCompletedForUser = (activity: ActivitySummary) =>
    userTeamIds.length > 0 && !!activity.completedActivityTeamIds?.some((teamId) => userTeamIds.includes(teamId))

  const isCurrentActivityOpen = !!currentActivity && !isCompletedForUser(currentActivity)
  if (isCurrentActivityOpen) return { activity: currentActivity, isFromFallback: false }

  // A game with no loops left to send is over, so it never stands in for the next activity.
  const isGameStillToPlay = (activity: ActivityWithSessions) =>
    isGameActivityType(activity.type) &&
    !isCompletedForUser(activity) &&
    !hasPlayedEveryConfiguredLoop({ numberOfLoops: activity.numberOfLoops, isLoopedGame: true })

  const fallbackGame = upcomingActivities.find(isGameStillToPlay)
  if (!fallbackGame) return null

  const fallbackSession =
    fallbackGame.sessions?.find((session) => userTeamIds.includes(session.teamId)) || fallbackGame.sessions?.[0]
  return { activity: { ...fallbackGame, session: fallbackSession }, isFromFallback: true }
}

/** A finished game leads straight into its AI debrief once the debrief starts within this time or has started. */
const AI_DEBRIEF_DIRECT_ENTRY_LEAD_MS = 10 * ONE_MINUTE_MS // 10 min

export type FinishedGame = { sprintId: string; activityId: string; teamId: string }

/** The team's next activity in the sprint of the game it just finished: the earliest one it has not completed. */
const findActivityAfterGame = (
  activities: ActivityWithSessions[],
  game: FinishedGame
): ActivityWithSessions | undefined => {
  const isStillAheadForTeam = (activity: ActivityWithSessions) =>
    activity.sprintId === game.sprintId &&
    activity.id !== game.activityId &&
    !activity.completedActivityTeamIds?.includes(game.teamId)

  return activities
    .filter(isStillAheadForTeam)
    .sort((first, second) => new Date(first.startDate).getTime() - new Date(second.startDate).getTime())[0]
}

/** The team's next activity is an AI debrief that starts within ten minutes or is already running. */
export const isAIDebriefDueAfterGame = (
  activities: ActivityWithSessions[],
  game: FinishedGame,
  nowMs: number
): boolean => {
  const nextActivity = findActivityAfterGame(activities, game)
  if (!nextActivity || !isAIDebriefActivity(nextActivity.type)) return false

  const timeUntilStartMs = new Date(nextActivity.startDate).getTime() - nowMs
  return timeUntilStartMs < AI_DEBRIEF_DIRECT_ENTRY_LEAD_MS
}
